import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { countryCities } from 'src/app/shared/data/countryCities.json';
import { ICountryCity } from 'src/app/shared/interfaces/country-city.interface';
import { RegExps } from 'src/app/shared/resources/regexps.constant';
import { routesData } from 'src/app/shared/resources/routes.constant';
import { CustomerDataService } from 'src/app/shared/services/customer-data.service';
import { GroupValidMatcher } from 'src/app/shared/utils/GroupValidMatcher';
import { CustomValidators } from 'src/app/shared/validators/custom-validators.validators';

@Component({
  selector: 'app-personal-information',
  templateUrl: './personal-information.component.html',
  styleUrls: ['./personal-information.component.scss']
})
export class PersonalInformationComponent implements OnInit, OnDestroy {
  public name: string;
  public maxDate = new Date();
  public personalInformationForm: UntypedFormGroup = {} as UntypedFormGroup;
  public filteredIdentificationCityOptions: Observable<ICountryCity[]> =
    new Observable();
  public filteredBirthCityOptions: Observable<ICountryCity[]> =
    new Observable();
  public normalizeForm = 'NFD';
  public confirmValidMatcher = new GroupValidMatcher();
  public subscriptions = new Subscription();
  public preloadCrmData: {
    birthdateInput: Date;
    birthCity: ICountryCity;
    genderInputGroup: string;
    idDatePickerInput: Date;
    idCity: ICountryCity;
  } = {
    birthdateInput: new Date(),
    birthCity: {} as ICountryCity,
    genderInputGroup: '',
    idDatePickerInput: new Date(),
    idCity: {} as ICountryCity
  };
  public months = [
    { value: 1, viewValue: 'Enero' },
    { value: 2, viewValue: 'Febrero' },
    { value: 3, viewValue: 'Marzo' },
    { value: 4, viewValue: 'Abril' },
    { value: 5, viewValue: 'Mayo' },
    { value: 6, viewValue: 'Junio' },
    { value: 7, viewValue: 'Julio' },
    { value: 8, viewValue: 'Agosto' },
    { value: 9, viewValue: 'Septiembre' },
    { value: 10, viewValue: 'Octubre' },
    { value: 11, viewValue: 'Noviembre' },
    { value: 12, viewValue: 'Diciembre' }
  ];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private customerDataService: CustomerDataService,
    private router: Router
  ) {
    this.name = this.customerDataService.customerData.firstName;
  }

  ngOnInit(): void {
    this.personalInformationForm = this.formBuilder.group({
      dateGroup: this.formBuilder.group(
        {
          day: ['', [Validators.required]],
          month: [null, [Validators.required]],
          year: ['', [Validators.required, CustomValidators.yearValidator()]]
        },
        {
          validators: [
            CustomValidators.monthsDayValidation(),
            CustomValidators.ageValidator(),
            CustomValidators.futureDateValidator(),
            CustomValidators.expeditionDateValidator(
              true,
              this.customerDataService.customerData.personalInformation?.idDate
            )
          ]
        }
      ),
      birthCity: [
        this.preloadCrmData.birthCity,
        [
          Validators.required,
          CustomValidators.notAString,
          CustomValidators.requiredCombo
        ]
      ],
      genderInputGroup: [
        this.preloadCrmData.genderInputGroup,
        [Validators.required]
      ],
      idDateGroup: this.formBuilder.group(
        {
          day: ['', [Validators.nullValidator]],
          month: [null, [Validators.nullValidator]],
          year: [
            '',
            [Validators.nullValidator, CustomValidators.yearValidator()]
          ]
        },
        {
          validators: [
            CustomValidators.monthsDayValidation(),
            CustomValidators.futureDateValidator(),
            CustomValidators.idDateValidator()
          ]
        }
      ),
      idCity: [
        this.preloadCrmData.idCity,
        [Validators.nullValidator, CustomValidators.notAString]
      ]
    });

    this.personalInformationForm.get('idDateGroup.day')!.disable();
    this.personalInformationForm.get('idDateGroup.month')!.disable();
    this.personalInformationForm.get('idDateGroup.year')!.disable();

    this.maxDate.setDate(this.maxDate.getDate() - 1);
    this.changeBirthDate();
    this.filteredBirthCityOptions = this.search('birthCity');
    this.filteredIdentificationCityOptions = this.search('idCity');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  datePickerClosedEmitter(input: string) {
    this.personalInformationForm.get(input)!.markAsTouched();
  }

  displayFn(city: ICountryCity): string {
    return city ? city.municipality : '';
  }

  closedEmitter(input: string) {
    const valuePossibleCity = this.personalInformationForm.get(input)!.value;
    const cityMaybe =
      typeof valuePossibleCity === 'string' && valuePossibleCity.length >= 3
        ? this._filter(valuePossibleCity)
        : null;
    if (cityMaybe?.length) {
      this.personalInformationForm.get(input)!.setValue(cityMaybe[0]);
    }
  }

  public changeBirthDate(): void {
    this.personalInformationForm
      .get('dateGroup')!
      .valueChanges.subscribe(() => {
        this.personalInformationForm.get('idDateGroup.day')!.setValue('');
        this.personalInformationForm.get('idDateGroup.month')!.setValue(null);
        this.personalInformationForm.get('idDateGroup.year')!.setValue('');

        this.personalInformationForm.get('idDateGroup.day')!.disable();
        this.personalInformationForm.get('idDateGroup.month')!.disable();
        this.personalInformationForm.get('idDateGroup.year')!.disable();
      });
  }

  public search(input: string): Observable<ICountryCity[]> {
    return this.personalInformationForm.get(input)!.valueChanges.pipe(
      startWith(''),
      map((value) =>
        typeof value === 'string' || value === null ? value : value.municipality
      ),
      map((name) => (name ? this._filter(name) : countryCities.slice()))
    );
  }

  public onSubmit(): void {
    if (this.personalInformationForm.valid) {
      const dateGroupValue = this.personalInformationForm.value.dateGroup;
      const birthdateInput = new Date(
        `${dateGroupValue.year}/${dateGroupValue.month}/${dateGroupValue.day}`
      );

      this.customerDataService.customerData = {
        ...this.customerDataService.customerData,
        personalInformation: {
          ...this.customerDataService.customerData.personalInformation,
          birthdate: birthdateInput,
          birthCity: this.personalInformationForm.value.birthCity,
          gender: this.personalInformationForm.value.genderInputGroup
        }
      };

      this.router.navigate([routesData.ECONOMIC_ACTIVITY.redirectPath]);
    }
  }

  private _filter(municipality: string): ICountryCity[] {
    const filterValue = municipality
      .toLowerCase()
      .normalize(this.normalizeForm)
      .replace(RegExps.DIACRITICAL_MARKS, '');

    return countryCities.filter((option) =>
      option.municipality
        .toLowerCase()
        .normalize(this.normalizeForm)
        .replace(RegExps.DIACRITICAL_MARKS, '')
        .includes(filterValue)
    );
  }
}
